import { db } from '../db/indexeddb';
import { shouldMakeAPICall } from '../utils/network';
import axios from 'axios';
import { API_ENDPOINT } from '../../services/api/resources';

/**
 * RFQ Repository
 * Handles all RFQ data operations with offline-first approach.
 *
 * DATA AUDIT (View / Edit from IndexedDB when offline):
 * - Stored from list API (buyer_rfq_full_view): each RFQ in db.rfqs has token, rfq_code,
 *   bid_from_date, bid_to_date, bidding_type, bid_status, select_vendor, items, rfq_bids,
 *   rfq_vendor_list. List does NOT put shipping_address*, country, state, city, pincode, bid_time on each RFQ.
 * - rfq_addresses: stored in db.cache (top-level from list API). transformListDataToEditFormat
 *   uses first rfq_address to fill address when RFQ has none.
 * - Bids/vendors: also persisted to db.rfqBids and db.cache (rfq_vendor_list) via _persistNestedBidsAndVendorLists.
 * - View needs: RFQ header, items, bids, vendors. Edit also needs address, bid_time; we fill from rfq_addresses when missing.
 */
export class RFQRepository {
  static _syncInFlight = new Map();
  static _listRequestsInFlight = new Map();

  _requestList(authToken, formData) {
    const key = JSON.stringify([authToken, Array.from(formData.entries())]);
    const pending = RFQRepository._listRequestsInFlight.get(key);
    if (pending) return pending;
    const request = axios({
      url: API_ENDPOINT.buyer_rfq_full_view,
      method: 'POST',
      data: formData,
      headers: { Authorization: `Bearer ${authToken}` },
    }).finally(() => RFQRepository._listRequestsInFlight.delete(key));
    RFQRepository._listRequestsInFlight.set(key, request);
    return request;
  }

  _scheduleNestedPersistence(rfqs, buyerToken = null) {
    setTimeout(() => {
      this._persistNestedBidsAndVendorLists(rfqs, buyerToken).catch((error) => {
        console.warn('Background RFQ nested cache persistence failed:', error);
      });
    }, 0);
  }
  

  _buildListRequestFormData(orgToken, pageNumber = 1, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    const formData = new FormData();
    formData.append('org_token', orgToken);
    if (pageNumber !== null) {
      formData.append('page', pageNumber);
    }
    if (searchQuery) formData.append('search', searchQuery);
    if (startDate) formData.append('start_date', startDate);
    if (endDate) formData.append('end_date', endDate);
    if (sortingColumn) formData.append('sorting_column_name', sortingColumn);
    if (sortingOrder) {
      formData.append(
        'sorting_order',
        sortingOrder === 'asc' ? 'ascending' : sortingOrder === 'desc' ? 'descending' : sortingOrder
      );
    }
    return formData;
  }

  _extractPaginationMeta(responseData, rfqs = []) {
    return {
      totalRFQs: responseData?.total_rfqs ?? responseData?.total_count ?? 0,
      pageCount: responseData?.page_count ?? 1,
      pageSize: responseData?.current_page_count ?? (Array.isArray(rfqs) ? rfqs.length : 0),
    };
  }

  /**
   * Get all RFQs from IndexedDB
   * @param {string} orgToken - Organization token
   * @param {number} pageNumber - Page number (optional, if provided returns only that page)
   * @param {string} searchQuery - Search query (optional, for filtering)
   * @param {string} startDate - Start date (optional, for filtering)
   * @param {string} endDate - End date (optional, for filtering)
   * @param {string} sortingColumn - Sorting column (optional, for filtering)
   * @param {string} sortingOrder - Sorting order (optional, for filtering)
   */
  async getAllFromCache(orgToken, pageNumber = null, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    try {
      let allRFQs;
      
      if (orgToken) {
        allRFQs = await db.rfqs.where('org_token').equals(orgToken).toArray();
      } else {
        allRFQs = await db.rfqs.toArray();
      }
      
      // If page number is specified, filter by page and filters
      if (pageNumber !== null) {
        // Filter by page and matching filters
        const filtered = allRFQs.filter(rfq => {
          const matchPage = rfq.page === pageNumber || rfq.page === undefined || rfq.page === null;
          const matchSearch = !searchQuery || !rfq.searchQuery || rfq.searchQuery === searchQuery;
          const matchStartDate = !startDate || !rfq.startDate || rfq.startDate === startDate;
          const matchEndDate = !endDate || !rfq.endDate || rfq.endDate === endDate;
          const matchSorting = (!sortingColumn || !rfq.sortingColumn || rfq.sortingColumn === sortingColumn) &&
                               (!sortingOrder || !rfq.sortingOrder || rfq.sortingOrder === sortingOrder);
          
          return matchPage && matchSearch && matchStartDate && matchEndDate && matchSorting;
        });
        
        // If exact match found, return that page
        if (filtered.length > 0) {
          console.log(`📦 Found ${filtered.length} RFQs in cache for page ${pageNumber}`);
          // Flatten data to ensure item_name, item_code, quantity are at top level for table display
          return filtered.map(rfq => this._flattenRFQForTable(rfq.data));
        }
        
        // If no exact match, return all RFQs for this org (fallback)
        console.log(`⚠️ No exact page match found, returning all cached RFQs`);
      }
      
      // Return all RFQs (for backward compatibility and when no page specified)
      // Flatten data to ensure item_name, item_code, quantity are at top level for table display
      return allRFQs.map(rfq => this._flattenRFQForTable(rfq.data));
    } catch (error) {
      console.error('Error reading RFQs from cache:', error);
      return [];
    }
  }

  /**
   * Save RFQs to IndexedDB with pagination support
   * @param {Array} rfqs - Array of RFQ objects
   * @param {string} orgToken - Organization token
   * @param {number} pageNumber - Page number (optional)
   * @param {string} searchQuery - Search query (optional)
   * @param {string} startDate - Start date (optional)
   * @param {string} endDate - End date (optional)
   * @param {string} sortingColumn - Sorting column (optional)
   * @param {string} sortingOrder - Sorting order (optional)
   */
  async saveToCache(rfqs, orgToken, pageNumber = null, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    try {
      const optimisticCount = rfqs.filter(rfq => rfq._pendingSync || rfq._lastUpdated).length;
      
      await db.transaction('rw', db.rfqs, async () => {
        // If page number is specified, only clear that specific page with matching filters
        if (pageNumber !== null && orgToken) {
          // Get all RFQs for this org
          const allRFQs = await db.rfqs.where('org_token').equals(orgToken).toArray();
          
          // Find RFQs that match this page and filters
          const toDelete = allRFQs.filter(rfq => {
            const matchPage = rfq.page === pageNumber || rfq.page === undefined || rfq.page === null;
            const matchSearch = !searchQuery || !rfq.searchQuery || rfq.searchQuery === searchQuery;
            const matchStartDate = !startDate || !rfq.startDate || rfq.startDate === startDate;
            const matchEndDate = !endDate || !rfq.endDate || rfq.endDate === endDate;
            const matchSorting = (!sortingColumn || !rfq.sortingColumn || rfq.sortingColumn === sortingColumn) &&
                                 (!sortingOrder || !rfq.sortingOrder || rfq.sortingOrder === sortingOrder);
            
            return matchPage && matchSearch && matchStartDate && matchEndDate && matchSorting;
          });
          
          // Delete matching RFQs
          if (toDelete.length > 0) {
            const idsToDelete = toDelete.map(rfq => rfq.id);
            await db.rfqs.bulkDelete(idsToDelete);
            console.log(`🗑️ Deleted ${idsToDelete.length} RFQs for page ${pageNumber}`);
          }
        } else {
          // Clear all data for this org if no page specified (backward compatibility)
          if (orgToken) {
            await db.rfqs.where('org_token').equals(orgToken).delete();
          } else {
            await db.rfqs.clear();
          }
        }
        
        // Insert new data - preserve optimistic updates and add pagination metadata
        const rfqData = rfqs.map(rfq => ({
          rfq_token: rfq.token || rfq.rfq_token || '',
          org_token: orgToken || rfq.org_token || '',
          page: pageNumber,
          searchQuery: searchQuery || '',
          startDate: startDate || '',
          endDate: endDate || '',
          sortingColumn: sortingColumn || '',
          sortingOrder: sortingOrder || '',
          data: rfq,
          lastSynced: Date.now(),
        }));
        
        await db.rfqs.bulkAdd(rfqData);
      });
      
      console.log(`✅ Cached ${rfqs.length} RFQs${pageNumber !== null ? ` (page ${pageNumber})` : ''} (${optimisticCount} with optimistic updates)`);
    } catch (error) {
      console.error('Error saving RFQs to cache:', error);
      // If bulkAdd fails (duplicate keys), try individual puts
      try {
        const optimisticCount = rfqs.filter(rfq => rfq._pendingSync || rfq._lastUpdated).length;
        for (const rfq of rfqs) {
          await db.rfqs.put({
            rfq_token: rfq.token || rfq.rfq_token || '',
            org_token: orgToken || rfq.org_token || '',
            page: pageNumber,
            searchQuery: searchQuery || '',
            startDate: startDate || '',
            endDate: endDate || '',
            sortingColumn: sortingColumn || '',
            sortingOrder: sortingOrder || '',
            data: rfq,
            lastSynced: Date.now(),
          });
        }
        console.log(`✅ Cached ${rfqs.length} RFQs using put (${optimisticCount} with optimistic updates)`);
      } catch (putError) {
        console.error('Error saving RFQs to cache with put:', putError);
      }
    }
  }


  /**
   * Get RFQ by token
   */
  async getByToken(rfqToken) {
    try {
      const rfq = await db.rfqs
        .where('rfq_token')
        .equals(rfqToken)
        .first();
      return rfq?.data || null;
    } catch (error) {
      console.error('Error getting RFQ by token:', error);
      return null;
    }
  }

  

  /**
   * Get RFQ by rfq_code (e.g. "PR-235/.../098" or "Q-RFQ_1780/...")
   * Used when edit is opened with selected_rfq_code (code) instead of token.
   */
  async getByRfqCode(orgToken, rfqCode) {
    if (!orgToken || !rfqCode || typeof rfqCode !== 'string') return null;
    try {
      const all = await this.getAllFromCache(orgToken, null, '', '', '', '', '');
      const found = all.find((r) => (r.rfq_code || '').trim() === rfqCode.trim());
      return found || null;
    } catch (error) {
      console.error('Error getting RFQ by rfq_code:', error);
      return null;
    }
  }

  // ---------- Explicit IndexedDB bindings (offline system) ----------
  // Use these when offline to bind data directly from IndexedDB, not from in-memory cache.

  /** Bind RFQ list from IndexedDB (db.rfqs). Use when offline. */
  async getRFQsFromIndexedDB(orgToken, pageNumber = null, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    return this.getAllFromCache(orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
  }

  /** Bind single RFQ by token from IndexedDB (db.rfqs). Use when offline. */
  async getRFQByTokenFromIndexedDB(rfqToken) {
    return this.getByToken(rfqToken);
  }

  /** Bind single RFQ by rfq_code from IndexedDB. Use when offline. */
  async getRFQByRfqCodeFromIndexedDB(orgToken, rfqCode) {
    return this.getByRfqCode(orgToken, rfqCode);
  }

  /** Bind bids for RFQ from IndexedDB (db.rfqBids). Use when offline. */
  async getBidsFromIndexedDB(rfqToken) {
    return this.getBidsFromCache(rfqToken);
  }

  /** Bind RFQ vendor list from IndexedDB (db.cache). Use when offline. */
  async getRFQVendorListFromIndexedDB(rfqToken) {
    return this.getRFQVendorListFromCache(rfqToken);
  }

  /** Bind RFQ addresses from IndexedDB (db.cache). Use when offline. */
  async getRFQAddressesFromIndexedDB(orgToken) {
    return this.getRFQAddressesFromCache(orgToken);
  }

  async getRFQPageFromCache(
    orgToken,
    pageNumber,
    searchQuery = '',
    startDate = '',
    endDate = '',
    sortingColumn = '',
    sortingOrder = ''
  ) {
    try {
      if (pageNumber == null) return null;
      let allRFQs = orgToken
        ? await db.rfqs.where('org_token').equals(orgToken).toArray()
        : await db.rfqs.toArray();

      const filtered = allRFQs.filter((rfq) => {
        const matchPage = rfq.page === pageNumber;
        const matchSearch = (rfq.searchQuery || '') === (searchQuery || '');
        const matchStartDate = (rfq.startDate || '') === (startDate || '');
        const matchEndDate = (rfq.endDate || '') === (endDate || '');
        const matchSorting =
          (rfq.sortingColumn || '') === (sortingColumn || '') &&
          (rfq.sortingOrder || '') === (sortingOrder || '');
        return matchPage && matchSearch && matchStartDate && matchEndDate && matchSorting;
      });

      if (filtered.length === 0) return null;

      const rows = filtered.map((rfq) => this._flattenRFQForTable(rfq.data));
      const pagination = await this.getPaginationFromCache(
        orgToken,
        pageNumber,
        searchQuery,
        startDate,
        endDate,
        sortingColumn,
        sortingOrder
      );

      return { rows, pagination };
    } catch (error) {
      console.error('Error reading RFQ page from cache:', error);
      return null;
    }
  }

  async saveRFQVendorListToCache(rfqToken, vendorList = []) {
    if (!rfqToken) return;
    try {
      await db.cache.put({
        key: `rfq_vendor_list_${rfqToken}`,
        value: Array.isArray(vendorList) ? vendorList : [],
        lastSynced: Date.now(),
      });
    } catch (error) {
      console.error('Error saving RFQ vendor list to cache:', error);
    }
  }

  async getRFQVendorListFromCache(rfqToken) {
    if (!rfqToken) return [];
    try {
      const cached = await db.cache.get(`rfq_vendor_list_${rfqToken}`);
      const value = cached?.value || [];
      return Array.isArray(value) ? value : [];
    } catch (error) {
      console.error('Error reading RFQ vendor list from cache:', error);
      return [];
    }
  }

  /**
   * Fetch RFQs from API and sync to cache
   */
  async syncFromAPI(authToken, orgToken, pageNumber = 1, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    if (!shouldMakeAPICall(authToken, orgToken)) {
      console.warn('⚠️ RFQ sync skipped - offline or not authenticated');
      return this.getAllFromCache(orgToken);
    }

    try {
      const effectivePage = pageNumber == null ? 1 : pageNumber;
      pageNumber = effectivePage;
      const formData = this._buildListRequestFormData(
        orgToken,
        pageNumber,
        searchQuery,
        startDate,
        endDate,
        sortingColumn,
        sortingOrder
      );

      console.log(`🔄 Calling buyer_rfq_full_view API with org_token: ${orgToken}${pageNumber !== null ? `, page: ${pageNumber}` : ' (all RFQs)'}`);
      
      const response = await this._requestList(authToken, formData);

      console.log(`📥 API Response structure:`, {
        hasData: !!response?.data,
        hasResults: !!response?.data?.results,
        hasResponse: !!response?.data?.response,
        resultsLength: response?.data?.results?.length,
        responseLength: response?.data?.response?.length,
        hasRFQAddresses: !!response?.data?.rfq_addresses,
        rfqAddressesLength: response?.data?.rfq_addresses?.length,
      });

      // Match current table implementation (src/hooks/useFetchData.js)
      const data = response?.data?.results || [];
      
      if (data.length === 0 && response?.data?.response) {
        console.warn('⚠️ No results found, but response exists. Trying response.data.response...');
        // Fallback: some APIs might return data in response instead of results
        const fallbackData = Array.isArray(response.data.response) ? response.data.response : [];
        if (fallbackData.length > 0) {
          console.log(`✅ Using fallback data: ${fallbackData.length} RFQs`);
          // Save pagination if available
          if (response?.data?.total_rfqs || response?.data?.page_count) {
            await this.savePaginationToCache(
              orgToken,
              pageNumber,
              response.data,
              searchQuery,
              startDate,
              endDate,
              sortingColumn,
              sortingOrder
            );
          }
          return this._processRFQData(fallbackData, orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
        }
      }
      
      // Save pagination metadata (includes rfq_addresses)
      if (response?.data?.total_rfqs || response?.data?.page_count) {
        console.log('📊 RFQ pagination from API:', {
          total_count: response?.data?.total_count,
          total_rfqs: response?.data?.total_rfqs,
          page_count: response?.data?.page_count,
          current_page_count: response?.data?.current_page_count,
        });
        await this.savePaginationToCache(
          orgToken,
          pageNumber,
          response.data,
          searchQuery,
          startDate,
          endDate,
          sortingColumn,
          sortingOrder
        );
      }
      
      // Store rfq_addresses separately for easy access
      if (response?.data?.rfq_addresses && Array.isArray(response.data.rfq_addresses)) {
        await this.saveRFQAddressesToCache(orgToken, response.data.rfq_addresses);
        console.log(`✅ Cached ${response.data.rfq_addresses.length} RFQ addresses`);
      }

      // Store buyer user details for chat & message UI



      
      if (data.length === 0) {
        console.warn(`⚠️ No RFQ data in API response for page ${pageNumber}`);
        console.log('Response data keys:', Object.keys(response?.data || {}));
        console.log('Response structure:', {
          total_rfqs: response?.data?.total_rfqs,
          page_count: response?.data?.page_count,
          results: response?.data?.results?.length,
          response: response?.data?.response?.length,
          rfq_addresses: response?.data?.rfq_addresses?.length,
        });
      }
      
      return this._processRFQData(data, orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
    } catch (error) {
      console.error('Error syncing RFQs from API:', error);
      return this.getAllFromCache(orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
    }
  }

  /**
   * Fetch a single page from API for server-side pagination (online only).
   * Returns { rfqs, totalRFQs, pageCount, pageSize }.
   */
  async fetchPageFromAPI(authToken, orgToken, pageNumber, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    if (!shouldMakeAPICall(authToken, orgToken)) {
      const cached = await this.getAllFromCache(orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
      const cachedPagination = await this.getPaginationFromCache(orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
      return {
        rfqs: Array.isArray(cached) ? cached : [],
        totalRFQs: cachedPagination?.total_rfqs ?? 0,
        pageCount: cachedPagination?.page_count ?? 1,
        pageSize: cachedPagination?.current_page_count ?? (Array.isArray(cached) ? cached.length : 0),
      };
    }

    try {
      const formData = this._buildListRequestFormData(
        orgToken,
        pageNumber,
        searchQuery,
        startDate,
        endDate,
        sortingColumn,
        sortingOrder
      );

      const response = await this._requestList(authToken, formData);

      const responseData = response?.data || {};
      const apiRows = Array.isArray(responseData?.results)
        ? responseData.results
        : (Array.isArray(responseData?.response) ? responseData.response : []);
      const rfqs = apiRows.map((rfq) => this._flattenRFQForTable(rfq));
      const pagination = this._extractPaginationMeta(responseData, rfqs);

      if (pagination.totalRFQs || pagination.pageCount) {
        this.savePaginationToCache(
          orgToken,
          pageNumber,
          responseData,
          searchQuery,
          startDate,
          endDate,
          sortingColumn,
          sortingOrder
        ).catch((error) => {
          console.warn('Background RFQ pagination cache failed:', error);
        });
      }

      if (Array.isArray(responseData?.rfq_addresses) && responseData.rfq_addresses.length > 0) {
        this.saveRFQAddressesToCache(orgToken, responseData.rfq_addresses).catch((error) => {
          console.warn('Background RFQ address cache failed:', error);
        });
      }

      this._processRFQData(
        apiRows,
        orgToken,
        pageNumber,
        searchQuery,
        startDate,
        endDate,
        sortingColumn,
        sortingOrder
      ).catch((error) => {
        console.warn('Background RFQ cache sync failed:', error);
      });

      return {
        rfqs,
        ...pagination,
      };
    } catch (error) {
      console.error('Error fetching RFQ page from API:', error);
      const cached = await this.getAllFromCache(orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
      const cachedPagination = await this.getPaginationFromCache(orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
      return {
        rfqs: Array.isArray(cached) ? cached : [],
        totalRFQs: cachedPagination?.total_rfqs ?? 0,
        pageCount: cachedPagination?.page_count ?? 1,
        pageSize: cachedPagination?.current_page_count ?? (Array.isArray(cached) ? cached.length : 0),
      };
    }
  }

  /**
   * Sync remaining pages (2..pageCount) in background for offline cache.
   * Call when online after fetching current page. Does not update UI — only fills IndexedDB.
   * Skips currentPage. Uses same filters as the initial fetch.
   */
  async syncRemainingPagesInBackground(
    authToken,
    orgToken,
    pageCount,
    currentPage,
    searchQuery = '',
    startDate = '',
    endDate = '',
    sortingColumn = '',
    sortingOrder = '',
    options = {}
  ) {
    if (!shouldMakeAPICall(authToken, orgToken) || !pageCount || pageCount < 2) return;

    const { force = false, expectedTotal = 0 } = options || {};
    const syncKey = `${orgToken || 'all'}`;

    if (RFQRepository._syncInFlight.get(syncKey)) {
      console.log('🔁 RFQ background sync already in-flight — skipping');
      return;
    }

    const cacheCount = await this.getRFQCountFromCache(orgToken);
    const cacheEmpty = cacheCount === 0;
    const lastSyncedPage = cacheEmpty ? 0 : await this.getLastSyncedPage(orgToken);
    const lastTotalPages = cacheEmpty ? 0 : await this.getLastTotalPages(orgToken);
    const lastTotalCount = cacheEmpty ? 0 : await this.getLastTotalCount(orgToken);
    const cacheComplete = cacheEmpty ? false : await this.getCacheComplete(orgToken);

    if (!force && cacheComplete && (expectedTotal ? cacheCount >= expectedTotal : cacheCount > 0)) {
      console.log('✅ RFQ cache marked complete — skipping background sync');
      return;
    }

    if (!force) {
      if (expectedTotal && cacheCount >= expectedTotal && lastSyncedPage >= pageCount) {
        console.log('? RFQ cache already complete ? skipping background sync');
        return;
      }
      if (expectedTotal && lastTotalCount && expectedTotal <= lastTotalCount && lastSyncedPage >= pageCount) {
        console.log('? RFQ cache already complete (total_count unchanged) ? skipping background sync');
        return;
      }
      if (lastTotalPages && pageCount <= lastTotalPages && lastSyncedPage >= pageCount) {
        console.log('? RFQ cache already complete ? skipping background sync');
        return;
      }
    }

    let pages = [];
    for (let p = 1; p <= pageCount; p++) {
      if (p === currentPage) continue;
      if (!force && !cacheEmpty && p <= lastSyncedPage) continue;
      pages.push(p);
    }

    if (force && pages.length === 0) {
      for (let p = 1; p <= pageCount; p++) if (p !== currentPage) pages.push(p);
    }

    if (pages.length === 0) {
      await this.setLastTotalPages(orgToken, pageCount);
      await this.setLastSyncedPage(orgToken, Math.max(lastSyncedPage, pageCount));
      if (expectedTotal) {
        await this.setLastTotalCount(orgToken, expectedTotal);
      }
      const currentCount = await this.getRFQCountFromCache(orgToken);
      await this.setCacheComplete(orgToken, expectedTotal ? currentCount >= expectedTotal : currentCount > 0);
      return;
    }

    console.log(`🔄 Background sync: ${pages.length} remaining pages (current ${currentPage}) for offline cache`);
    // Cache checks above await IndexedDB; another caller may have started meanwhile.
    if (RFQRepository._syncInFlight.get(syncKey)) return;
    RFQRepository._syncInFlight.set(syncKey, true);
    try {
      for (const p of pages) {
        try {
          await this.syncFromAPI(authToken, orgToken, p, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
          await this.setLastSyncedPage(orgToken, p);
          await new Promise((r) => setTimeout(r, 300));
        } catch (e) {
          console.warn(`Background sync page ${p} failed:`, e?.message || e);
        }
      }

      await this.setLastTotalPages(orgToken, pageCount);
      if (expectedTotal) {
        await this.setLastTotalCount(orgToken, expectedTotal);
      }
      const currentCount = await this.getRFQCountFromCache(orgToken);
      await this.setCacheComplete(orgToken, expectedTotal ? currentCount >= expectedTotal : currentCount > 0);
      await this.setLastSyncedPage(orgToken, Math.max(lastSyncedPage, pageCount));
      console.log('✅ Background sync complete');
    } finally {
      RFQRepository._syncInFlight.delete(syncKey);
    }
  }

  async setLastSyncedPage(orgToken, page) {
    await db.syncMeta.put({
      key: `rfq_last_page_${orgToken || 'all'}`,
      value: page,
    });
  }

  async getLastSyncedPage(orgToken) {
    const res = await db.syncMeta.get(`rfq_last_page_${orgToken || 'all'}`);
    return res?.value || 0;
  }

  async setLastTotalPages(orgToken, totalPages) {
    await db.syncMeta.put({
      key: `rfq_last_total_pages_${orgToken || 'all'}`,
      value: totalPages,
    });
  }

  async getLastTotalPages(orgToken) {
    const res = await db.syncMeta.get(`rfq_last_total_pages_${orgToken || 'all'}`);
    return res?.value || 0;
  }

  async setLastTotalCount(orgToken, totalCount) {
    await db.syncMeta.put({
      key: `rfq_last_total_count_${orgToken || 'all'}`,
      value: totalCount,
    });
  }

  async getLastTotalCount(orgToken) {
    const res = await db.syncMeta.get(`rfq_last_total_count_${orgToken || 'all'}`);
    return res?.value || 0;
  }

  async setCacheComplete(orgToken, value) {
    await db.syncMeta.put({
      key: `rfq_cache_complete_${orgToken || 'all'}`,
      value: Boolean(value),
    });
  }

  async getCacheComplete(orgToken) {
    const res = await db.syncMeta.get(`rfq_cache_complete_${orgToken || 'all'}`);
    return Boolean(res?.value);
  }


async getOfflineChatParticipants(rfqToken) {
  try {
    const rfq = await this.getByToken(rfqToken);
    if (!rfq) return { vendors: [], users: [] };

    let vendors = [];
    let users = [];

    /* ---------------- USERS ---------------- */
    const allUsers = await db.users.toArray();

    users = allUsers.map(u => ({
      ...u.data,
      isBuyer: u.data?.role?.toLowerCase() === 'buyer'
    }));

    /* ---------------- VENDORS ---------------- */
    if (Array.isArray(rfq.rfq_vendor_list)) {
      const vendorTokens = rfq.rfq_vendor_list.map(
        v => v.vendor_token || v.token
      );

      const allVendors = await db.vendors.toArray();

      vendors = allVendors
        .map(v => v.data)
        .filter(v => vendorTokens.includes(v.token));
    }

    return { vendors, users };
  } catch (err) {
    console.error('Offline chat participant error:', err);
    return { vendors: [], users: [] };
  }
}







  /**
   * Save messages to cache
   */
  async saveMessagesToCache(rfqToken, messages) {
    try {
      await db.transaction('rw', db.rfqMessages, async () => {
        // Clear existing messages for this RFQ
        await db.rfqMessages.where('rfq_token').equals(rfqToken).delete();
        
        // Insert new messages
        const messageData = messages.map(msg => ({
          rfq_token: rfqToken,
          message_token: msg.token || msg.message_token || '',
          data: msg,
          lastSynced: Date.now(),
        }));
        
        await db.rfqMessages.bulkAdd(messageData);
      });
      
      console.log(`✅ Cached ${messages.length} messages for RFQ ${rfqToken}`);
    } catch (error) {
      console.error('Error saving messages to cache:', error);
    }
  }

  /**
   * Save bids to cache
   */
  async saveBidsToCache(rfqToken, bids) {
    try {
      await db.transaction('rw', db.rfqBids, async () => {
        await db.rfqBids.where('rfq_token').equals(rfqToken).delete();

        const bidData = (Array.isArray(bids) ? bids : []).map((bid) => ({
          rfq_token: rfqToken,
          bid_token: bid.token || bid.bid_token || '',
          data: bid,
          lastSynced: Date.now(),
        }));

        if (bidData.length > 0) {
          await db.rfqBids.bulkAdd(bidData);
        }
      });

      console.log(`✅ Cached ${Array.isArray(bids) ? bids.length : 0} bids for RFQ ${rfqToken}`);
    } catch (error) {
      console.error('Error saving bids to cache:', error);
    }
  }

  /**
   * Get bids from cache
   */
  async getBidsFromCache(rfqToken) {
    try {
      const rows = await db.rfqBids.where('rfq_token').equals(rfqToken).toArray();
      return rows.map((r) => r.data);
    } catch (error) {
      console.error('Error reading bids from cache:', error);
      return [];
    }
  }

  /**
   * Sync messages from API
   */
  async syncMessagesFromAPI(authToken, rfqToken, pageNumber = 1) {
    if (!shouldMakeAPICall(authToken, authToken)) {
      return this.getMessagesFromCache(rfqToken);
    }

    try {
      const formData = new FormData();
      formData.append('rfq_token', rfqToken);
      formData.append('page', pageNumber);
      formData.append('page_size', 10);

      const response = await axios({
        method: 'POST',
        url: API_ENDPOINT.list_of_msg,
        data: formData,
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      const messages = response?.data?.response || [];
      await this.saveMessagesToCache(rfqToken, Array.isArray(messages) ? messages : []);
      return messages;
    } catch (error) {
      console.error('Error syncing messages from API:', error);
      return this.getMessagesFromCache(rfqToken);
    }
  }

  /**
   * Get chat timeline from cache
   */
  async getChatTimelineFromCache(rfqToken, vendorToken = null, userToken = null) {
    try {
      let query = db.rfqChatTimelines.where('rfq_token').equals(rfqToken);
      
      if (vendorToken) {
        query = query.filter(t => t.vendor_token === vendorToken);
      }
      if (userToken) {
        query = query.filter(t => t.user_token === userToken);
      }
      
      const timelines = await query.toArray();
      return timelines.map(t => t.data);
    } catch (error) {
      console.error('Error reading chat timeline from cache:', error);
      return [];
    }
  }

  /**
   * Save chat timeline to cache
   */
  async saveChatTimelineToCache(rfqToken, timeline, vendorToken = null, userToken = null) {
    try {
      await db.transaction('rw', db.rfqChatTimelines, async () => {
        // Clear existing timeline
        let query = db.rfqChatTimelines.where('rfq_token').equals(rfqToken);
        if (vendorToken) {
          query = query.filter(t => t.vendor_token === vendorToken);
        }
        if (userToken) {
          query = query.filter(t => t.user_token === userToken);
        }
        await query.delete();
        
        // Insert new timeline
        const timelineData = Array.isArray(timeline) ? timeline : [timeline];
        const data = timelineData.map((item, index) => ({
          rfq_token: rfqToken,
          vendor_token: vendorToken || '',
          user_token: userToken || '',
          message_id: item.id || item.token || index,
          data: item,
          lastSynced: Date.now(),
        }));
        
        await db.rfqChatTimelines.bulkAdd(data);
      });
      
      console.log(`✅ Cached chat timeline for RFQ--------------- ${rfqToken}`);
    } catch (error) {
      console.error('Error saving chat timeline to cache:', error);
    }
  }

  /**
   * Sync chat timeline from API
   */
  async syncChatTimelineFromAPI(authToken, rfqToken, buyerToken, vendorToken = null) {
    if (!shouldMakeAPICall(authToken, buyerToken)) {
      return this.getChatTimelineFromCache(rfqToken, vendorToken, buyerToken);
    }

    try {
      const response = await axios({
        method: 'POST',
        url: API_ENDPOINT.view_rfq_chat,
        data: {
          buyer_token: buyerToken,
          vendor_token: vendorToken,
          rfq_token: rfqToken,
        },
        headers: {
          Authorization: `Bearer ${authToken}`,
        },
      });

      let timeline = response?.data?.response || [];
      
      // If there are additional timeline messages, fetch them
      const rfqExists = timeline.some(chat => chat.rfq);
      if (rfqExists) {
        const rfqId = timeline.find(chat => chat.rfq)?.rfq;
        try {
          const additionalResponse = await axios({
            method: 'POST',
            url: API_ENDPOINT.rfq_timeline_messages,
            data: {
              buyer: buyerToken,
              rfq_id: rfqId,
            },
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          });
          
          // Merge and sort by date
          const parseDate = (dateStr) => {
            if (!dateStr || typeof dateStr !== 'string') return new Date(0);
            try {
              if (dateStr.includes(' - ')) {
                const [datePart, timePart] = dateStr.split(' - ');
                return new Date(`${datePart.trim()} ${timePart.trim()}`);
              }
              return new Date(dateStr.trim());
            } catch {
              return new Date(0);
            }
          };
          
          const formattedTimeline = timeline.map(msg => ({
            ...msg,
            created: parseDate(msg.created_date),
          }));
          
          const formattedAdditional = (additionalResponse.data || []).map(msg => ({
            ...msg,
            created: parseDate(msg.created),
          }));
          
          timeline = [...formattedTimeline, ...formattedAdditional].sort((a, b) => a.created - b.created);
        } catch (err) {
          console.warn('Could not fetch additional timeline messages:', err);
        }
      }
      
      await this.saveChatTimelineToCache(rfqToken, timeline, vendorToken, buyerToken);
      return timeline;
    } catch (error) {
      console.error('Error syncing chat timeline from API:', error);
      return this.getChatTimelineFromCache(rfqToken, vendorToken, buyerToken);
    }
  }

  /**
   * Process RFQ data from API response
   * When pageNumber is null, API returns all RFQs - we save all without page filtering
   */
  /**
   * Extract item_name, item_code, quantity from items array for table display
   * This ensures the table can display these fields even when they're nested in items array
   */
  _flattenRFQForTable(rfq) {
    // If item_name, item_code, quantity already exist at top level, return as-is
    if (rfq.item_name && rfq.item_code && rfq.quantity) {
      return rfq;
    }
    
    // Extract from items array if available
    if (rfq.items && Array.isArray(rfq.items) && rfq.items.length > 0) {
      const firstItem = rfq.items[0];
      const itemCount = rfq.items.length;
      
      return {
        ...rfq,
        // Extract from first item if single item, otherwise show "Multiple Items"
        item_name: rfq.item_name || (itemCount === 1 ? firstItem.item_name : 'Multiple Items'),
        item_code: rfq.item_code || (itemCount === 1 ? firstItem.item_code : 'Multiple Items'),
        quantity: rfq.quantity || (itemCount === 1 ? firstItem.quantity : 'Multiple Items'),
        unit: rfq.unit || (itemCount === 1 ? firstItem.unit : 'Multiple Items'),
      };
    }
    
    // If no items array, return as-is (might be missing data)
    return rfq;
  }

  /**
   * Persist nested rfq_bids and rfq_vendor_list from list API into their caches.
   * Enables RFQ view to load bids/vendors when opening from list (offline or cached).
   */
  async _persistNestedBidsAndVendorLists(rfqs, buyerToken = null) {
    if (!rfqs || !Array.isArray(rfqs)) return;
    for (const rfq of rfqs) {
      const token = rfq.token || rfq.rfq_token;
      if (!token) continue;
      if (rfq.rfq_bids && Array.isArray(rfq.rfq_bids) && rfq.rfq_bids.length > 0) {
        await this.saveBidsToCache(token, rfq.rfq_bids);
      }
      if (rfq.rfq_vendor_list && Array.isArray(rfq.rfq_vendor_list) && rfq.rfq_vendor_list.length > 0) {
        await this.saveRFQVendorListToCache(token, rfq.rfq_vendor_list);
        // Also persist vendor chat timelines by default (if provided in list response)
        for (const vendor of rfq.rfq_vendor_list) {
          const vendorToken = vendor?.vendor_token || vendor?.token;
          const chat = vendor?.chat;
          if (!vendorToken || !Array.isArray(chat) || chat.length === 0) continue;
          await this.saveChatTimelineToCache(token, chat, vendorToken, buyerToken);
        }
      }
    }
  }

  async _processRFQData(data, orgToken, pageNumber = null, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    // Flatten RFQ data to ensure item_name, item_code, quantity are at top level for table display
    const flattenedData = data.map(rfq => this._flattenRFQForTable(rfq));

    // If pageNumber is null, API returned all RFQs - replace all cached data
    // Otherwise, preserve optimistic updates and merge with existing cache
    if (pageNumber === null) {
      // API returned all RFQs - save all of them, clearing old data
      console.log(`📦 API returned all RFQs (${flattenedData.length} total) - caching all for offline use`);
      await this.saveToCache(flattenedData, orgToken, null, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
      this._scheduleNestedPersistence(flattenedData, orgToken);
      return flattenedData;
    }

    // Page-specific sync: preserve optimistic updates from all pages.
    // Do not dedupe current page rows by token; API can return multiple rows with same token.
    const existingCache = await this.getAllFromCache(orgToken, null); // null = get all pages
    const optimisticRFQs = existingCache.filter(rfq => rfq._pendingSync || rfq._lastUpdated);

    const currentPageTokens = new Set(
      flattenedData
        .map((rfq) => rfq.token || rfq.rfq_token)
        .filter(Boolean)
    );

    const optimisticToAppend = optimisticRFQs.filter((rfq) => {
      const token = rfq.token || rfq.rfq_token;
      // Keep optimistic entries without token and avoid duplicating tokened rows already in current page.
      if (!token) return true;
      return !currentPageTokens.has(token);
    });

    const flattenedMergedRFQs = [...flattenedData, ...optimisticToAppend];
    await this.saveToCache(flattenedMergedRFQs, orgToken, pageNumber, searchQuery, startDate, endDate, sortingColumn, sortingOrder);
    this._scheduleNestedPersistence(flattenedMergedRFQs, orgToken);

    console.log(`✅ Synced ${flattenedMergedRFQs.length} RFQs (page ${pageNumber}) (${optimisticRFQs.length} optimistic updates preserved)`);
    return flattenedMergedRFQs;
  }

  /**
   * Get last sync time for RFQs
   */
  async getLastSyncTime(orgToken) {
    try {
      const rfq = orgToken
        ? await db.rfqs.where('org_token').equals(orgToken).orderBy('lastSynced').last()
        : await db.rfqs.orderBy('lastSynced').last();
      return rfq?.lastSynced || null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Get cached RFQ count for an org
   */
  async getRFQCountFromCache(orgToken) {
    try {
      if (orgToken) {
        return await db.rfqs.where('org_token').equals(orgToken).count();
      }
      return await db.rfqs.count();
    } catch (error) {
      console.error('Error counting RFQs from cache:', error);
      return 0;
    }
  }

  async getCachedPageCount(orgToken) {
    try {
      const all = orgToken
        ? await db.rfqs.where('org_token').equals(orgToken).toArray()
        : await db.rfqs.toArray();
      const pages = new Set();
      for (const row of all) {
        if (row.searchQuery || row.startDate || row.endDate || row.sortingColumn || row.sortingOrder) continue;
        if (row.page != null) pages.add(row.page);
      }
      return pages.size;
    } catch (error) {
      console.error('Error counting RFQ cached pages:', error);
      return 0;
    }
  }

  /**
   * Save pagination metadata
   */
  async savePaginationToCache(orgToken, pageNumber, paginationData, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    try {
      const key = `${orgToken}_${pageNumber}_${searchQuery}_${startDate}_${endDate}_${sortingColumn}_${sortingOrder}`;
      
      await db.rfqPagination.put({
        org_token: orgToken,
        page: pageNumber,
        searchQuery: searchQuery || '',
        startDate: startDate || '',
        endDate: endDate || '',
        sortingColumn: sortingColumn || '',
        sortingOrder: sortingOrder || '',
        total_rfqs: paginationData?.total_rfqs ?? paginationData?.total_count ?? 0,
        current_page_count: paginationData?.current_page_count || 0,
        page_count: paginationData?.page_count || 0,
        lastSynced: Date.now(),
      });
      
      console.log(`✅ Cached pagination for page ${pageNumber}`);
    } catch (error) {
      console.error('Error saving pagination to cache:', error);
    }
  }

  /**
   * Get pagination metadata from cache
   */
  async getPaginationFromCache(orgToken, pageNumber, searchQuery = '', startDate = '', endDate = '', sortingColumn = '', sortingOrder = '') {
    try {
      const allPagination = await db.rfqPagination
        .where('org_token').equals(orgToken)
        .toArray();
      
      // Filter by matching criteria
      const pagination = allPagination.find(p => 
        p.page === pageNumber &&
        (!searchQuery || !p.searchQuery || p.searchQuery === searchQuery) &&
        (!startDate || !p.startDate || p.startDate === startDate) &&
        (!endDate || !p.endDate || p.endDate === endDate) &&
        (!sortingColumn || !p.sortingColumn || p.sortingColumn === sortingColumn) &&
        (!sortingOrder || !p.sortingOrder || p.sortingOrder === sortingOrder)
      );
      
      if (pagination) {
        return {
          total_rfqs: pagination.total_rfqs,
          current_page_count: pagination.current_page_count,
          page_count: pagination.page_count,
        };
      }
      return null;
    } catch (error) {
      console.error('Error reading pagination from cache:', error);
      return null;
    }
  }

  /**
   * Check if RFQ data from list has all required fields for editing
   * @param {Object} rfqData - RFQ object from the list API
   * @returns {boolean} - True if all required fields are present
   */
  hasAllRequiredFieldsForEdit(rfqData) {
    if (!rfqData) return false;

    // List API (buyer_rfq_full_view) does not put address or bid_time on each RFQ; those come from rfq_addresses or individual API.
    // We only require what the list always has. transformListDataToEditFormat fills address from rfq_addresses when missing.
    const requiredBasicFields = [
      'token',
      'bidding_type',
      'bid_status',
      'bid_from_date',
      'bid_to_date',
      'bid_time',
      'select_vendor',
    ];

    for (const field of requiredBasicFields) {
      const v = rfqData[field];
      if (v === undefined || v === null) {
        console.log(`❌ Missing required field for edit: ${field}`);
        return false;
      }
      // select_vendor can be ''; others must be non-empty
      if (field !== 'select_vendor' && typeof v === 'string' && v.trim() === '') {
        console.log(`❌ Missing required field for edit: ${field}`);
        return false;
      }
    }

    // bid_time, shipping_address1, country, state, city: optional for list data; transform fills from rfq_addresses

    // Check items array - must exist and have at least one item
    if (!rfqData.items || !Array.isArray(rfqData.items) || rfqData.items.length === 0) {
      console.log('❌ Missing or empty items array');
      return false;
    }

    // Item fields: description can be empty; others required
    const requiredItemFields = [
      'item_token',
      'item_mapped_token',
      'item_code',
      'item_name',
      'quantity',
      'unit',
    ];

    for (const item of rfqData.items) {
      for (const field of requiredItemFields) {
        if (item[field] === undefined || item[field] === null || item[field] === '') {
          console.log(`❌ Missing required item field for edit: ${field} in item ${item.item_code || item.item_token}`);
          return false;
        }
      }
      // description can be empty
    }

    console.log('✅ RFQ data has all required fields for editing');
    return true;
  }

  /**
   * Save RFQ addresses from API response
   * @param {string} orgToken - Organization token
   * @param {Array} addresses - Array of shipping addresses
   */
  async saveRFQAddressesToCache(orgToken, addresses) {
    try {
      // Store in a way that's accessible - we can use the shippingAddresses store
      // or store it with pagination metadata
      // For now, store it in the pagination metadata as it's per-org
      if (addresses && Array.isArray(addresses) && addresses.length > 0) {
        // Store in a separate cache entry for easy retrieval
        await db.cache.put({
          key: `rfq_addresses_${orgToken}`,
          value: addresses,
          lastSynced: Date.now(),
        });
      }
    } catch (error) {
      console.error('Error saving RFQ addresses to cache:', error);
    }
  }

  /**
   * Get RFQ addresses from cache
   * @param {string} orgToken - Organization token
   * @returns {Array} - Array of shipping addresses
   */
  async getRFQAddressesFromCache(orgToken) {
    try {
      const cached = await db.cache.get(`rfq_addresses_${orgToken}`);
      return cached?.value || [];
    } catch (error) {
      console.error('Error getting RFQ addresses from cache:', error);
      return [];
    }
  }

  /**
   * Transform RFQ list data to match individual RFQ API response format
   * This allows using list data directly for editing
   * @param {Object} rfqData - RFQ object from list API
   * @param {string} orgToken - Organization token (for getting rfq_addresses)
   * @returns {Object} - Transformed data matching individual API format
   */
  async transformListDataToEditFormat(rfqData, orgToken = null) {
    if (!rfqData) return null;

    // Get rfq_addresses from IndexedDB (list API returns them at top level, not per RFQ)
    let rfqAddresses = [];
    if (orgToken) {
      rfqAddresses = await this.getRFQAddressesFromCache(orgToken);
    }

    // List API often omits address fields and bid_time on each RFQ. Use first rfq_address when missing.
    const addr = rfqAddresses && rfqAddresses.length > 0 ? rfqAddresses[0] : {};
    const shipping_address1 = rfqData.shipping_address1 || addr.shipping_address1 || '';
    const shipping_address2 = rfqData.shipping_address2 || addr.shipping_address2 || '';
    const country = rfqData.country || addr.country || '';
    const state = rfqData.state || addr.state || '';
    const city = rfqData.city || addr.city || '';
    const pincode = rfqData.pincode ?? addr.pincode ?? '';
    const mobile_number = rfqData.mobile_number ?? addr.mobile_number ?? '';

    return {
      response: [
        {
          token: rfqData.token,
          rfq_code: rfqData.rfq_code,
          purchase_request_number: rfqData.purchase_request_number || '',
          bidding_type: rfqData.bidding_type,
          bid_status: rfqData.bid_status,
          bid_from_date: rfqData.bid_from_date,
          bid_to_date: rfqData.bid_to_date,
          bid_time: rfqData.bid_time || '', // list API often omits
          select_vendor: rfqData.select_vendor || '',
          shipping_address1,
          shipping_address2,
          country,
          state,
          city,
          pincode,
          mobile_number,
          proposal_document: rfqData.proposal_document || '',
          proposal_description: rfqData.proposal_description || null,
          sealed_bid: rfqData.sealed_bid,
          locked_bid_count: rfqData.locked_bid_count,
          next_unlock_at: rfqData.next_unlock_at,
          // Preserve expense_type if list API provides it (used as fallback when item-level is missing)
          expense_type: rfqData.expense_type ?? rfqData.expenseType ?? '',
          category_compare:
            rfqData.category_compare ??
            rfqData.rfq_category?.id ??
            null,
          category_compare_name:
            rfqData.category_compare_name ??
            rfqData.rfq_category?.name ??
            null,
          rfq_category: rfqData.rfq_category || null,
          item_description_doc: rfqData.item_description_doc
        },
      ],
      items: rfqData.items.map((item) => ({
        item_token: item.item_token,
        item_mapped_token: item.item_mapped_token,
        item_code: item.item_code,
        item_name: item.item_name,
        // List API includes expense_type per item; keep it for view/edit rendering.
        expense_type:
          item.expense_type ??
          item.expenseType ??
          rfqData.expense_type ??
          rfqData.expenseType ??
          '',
        quantity: item.quantity,
        unit: item.unit,
        description: item.description || '',
        part_number: item.part_number || 'nan',
        hsn_code: item.hsn_code || '',
        unspsc_code: item.unspsc_code || null,
        normalized_value: item.normalized_value || '',
        category: item.category || '',
      })),
      rfq_addresses: rfqAddresses,
    };
  }
}

export const rfqRepository = new RFQRepository();
